import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { jwtDecode } from "jwt-decode";
import { LocalStorageService } from './local-storage.service';
import { environment } from 'src/environments/environment';


/**
 * Provides all methods for making HTTP requests to the server regarding authentication and submitting data.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {

  constructor(private dataSync: LocalStorageService, private http: HttpClient) {}

  /**
   * Handle an error, that is throw it.
   * @param error The error response
   * @returns 
   */
  handleError(error: HttpErrorResponse): Observable<never> {
    return throwError(error);
  }


  /**
   * Sign up a new user.
   * @param json All user data for a sign up
   * @returns Server response wrapped in an Observable.
   */
  postSignUp(json: any): Observable<any> {
    return this.http.post<any>(`${environment.apiURL}/Auth/signup`, json, {timeout: 7000, headers: new HttpHeaders({'Content-Type': 'application/json'})});
  }

   /**
   * Attempt to login the user.
   * @param json Username and password
   * @returns Server response wrapped in an Observable.
   */
  postLogin(json: any): Observable<any> {
    return this.http.post<any>(`${environment.apiURL}/Auth/login`, json, {timeout: 7000, headers: {'Content-Type': 'application/json'}});
  }

   /**
   * Update a user's profile.
   * @param json User profile data
   * @returns Server response wrapped in an Observable.
   */
  postProfileUpdate(json: any): Observable<any> {
    return this.http.post<any>(`${environment.apiURL}/Auth/update`, json, {timeout: 7000, headers: {'Authorization': 'Bearer ' + this.dataSync.accessToken(), 'Content-Type': 'application/json'}})
  }
 
   /**
   * Forgotten username.
   * @param email Username is sent to this email
   * @returns Server response wrapped in an Observable.
   */
  postForgotUsername(email: string): Observable<any> {
    return this.http.post<any>(`${environment.apiURL}/Auth/forgot-username`, { "Email": email }, {timeout: 7000, headers: { 'Content-Type': 'application/json' } }).pipe(catchError(this.handleError));
  }

   /**
   * Forgotten password.
   * @param email Reset code is sent to this email
   * @returns Server response wrapped in an Observable.
   */
  postForgotPassword(email: string): Observable<any> {
    return this.http.post<any>(`${environment.apiURL}/Auth/forgot-password`, { "email": email }, {timeout: 7000, headers: { 'Content-Type': 'application/json' } }).pipe(catchError(this.handleError));
  }

   /**
   * Reset a password.
   * @param json Reset code and new password
   * @returns Server response wrapped in an Observable.
   */
  postResetPassword(json: any): Observable<any> {
    return this.http.post<any>(`${environment.apiURL}/Auth/reset-password-code`, json, {timeout: 7000, headers: {'Content-Type': 'application/json'}});
  }

   /**
   * Refresh the current {@link this.dataSync.accessToken()} using the current {@link LocalStorageService.refreshToken}
   * @returns Server response wrapped in an Observable.
   */
  postRefreshToken(): Observable<any> {
    return this.http.post<any>(`${environment.apiURL}/Auth/refresh-token?token=${this.dataSync.refreshToken()}`, '', {timeout: 7000});
  }

   /**
   * Revoke the current {@link this.dataSync.accessToken()}
   * @returns Server response wrapped in an Observable.
   */
  postRevokeToken(): Observable<any> {
    return this.http.post<any>(`${environment.apiURL}/Auth/revoke-token`, '', {timeout: 7000, headers: new HttpHeaders({'Authorization': 'Bearer ' + this.dataSync.accessToken()}), responseType: 'text'} as Object);
  }

 /**
   * Vessels are recognized by their number determined by the server.
   * @param vesselNumber The vessel number to search for
   * @returns Server response wrapped in an Observable.
   */
  postVesselSearch(vesselNumber: string): Observable<any> {
    // Check for valid token
    return this.http.post<any>(`${environment.apiURL}/Vessel/Search`, {"vesselNumber": vesselNumber}, {timeout: 7000, headers: {'Authorization': 'Bearer ' + this.dataSync.accessToken(), 'Content-Type': 'application/json'}})
  }

   /**
   * Submit the current landing.
   * @param logbook Landing with other required info
   * @returns Server response wrapped in an Observable.
   */
  postSubmitLogbook(logbook: any): Observable<any> {
    return this.http.post<any>(`${environment.apiURL}/landinglog/submit`, logbook, {timeout: 30000, headers: {'Authorization': 'Bearer ' + this.dataSync.accessToken(), 'Content-Type': 'application/json'}});
 }

 /**
   * Post the help form from {@link }
   * @param json Help form data
   * @returns Server response wrapped in an Observable.
   */
 postHelpForm(json: any): Observable<any> {
  return this.http.post<any>(`${environment.apiURL}/contact/help-request`, json, {timeout: 7000, headers: {'Authorization': 'Bearer ' + this.dataSync.accessToken(), 'Content-Type': 'application/json'}});
 }

 /**
  * Check if the user's refresh token has expired. 
  * @returns Expiration status of the current {@link this.dataSync.accessToken()}
  */
  checkAccessToken(): {expired: boolean} {
    if (this.dataSync.accessToken() == "") return {expired: true};
    try {
      var decoded: any = jwtDecode(this.dataSync.accessToken());
      return {expired: ((new Date(decoded.exp * 1000)).toISOString() <= new Date().toISOString())};
    } catch {
      return {expired: true};
    }
  }

 /**
  * Check if the user's refresh token has expired. 
  * @returns Expiration status of the current {@link LocalStorageService.refreshToken}
  */
  checkRefreshToken(): {expired: boolean} {
    if (this.dataSync.refreshToken() == "") return {expired: true};
    try {
      var decoded: any = jwtDecode(this.dataSync.refreshToken());
      return {expired: ((new Date(decoded.exp * 1000)).toISOString() <= new Date().toISOString())};
    } catch {
      return {expired: true};
    }   
  }
}
